/**
 * Login Dice Scene
 * Lightweight Three.js scene showing 3 stacked dice (DSS01 model)
 * with a gently panning spotlight for subtle color shifts.
 *
 * Uses the SharedRenderer singleton instead of creating its own WebGL context.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SharedRenderer } from '../../engine/render/SharedRenderer';

const DICE_GLB_PATH = '/assets/models/dice/DSS01.glb';
const DICE_SCALE = 0.53;
const SPOT_PAN_RADIUS = 1.0;
const SPOT_PAN_SPEED = 0.28;
const SPOT_PAN_Y_RATIO = 0.1;


export class LoginDiceScene {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private diceGroup: THREE.Group;
  private spotlight: THREE.SpotLight;
  private clock: THREE.Clock;
  private viewId: string | null = null;
  private envMap: THREE.Texture | null = null;
  private shared: SharedRenderer;
  public readonly ready: Promise<void>;

  constructor(private container: HTMLElement) {
    this.shared = SharedRenderer.instance;

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(35, 1, 0.1, 50);
    this.camera.position.set(0, 3.5, 6);
    this.camera.lookAt(0, 0.6, 0);

    // Dice group (populated async)
    this.diceGroup = new THREE.Group();
    this.scene.add(this.diceGroup);

    // Shadow floor
    const floorGeo = new THREE.PlaneGeometry(12, 12);
    const floorMat = new THREE.ShadowMaterial({ opacity: 0.35 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.01;
    floor.receiveShadow = true;
    this.scene.add(floor);

    // Stationary spotlight
    this.spotlight = new THREE.SpotLight(0xffeedd, 80, 20, (32 * Math.PI) / 180, 0.5, 1.5);
    this.spotlight.position.set(1.6, 5, 5.1);
    this.spotlight.target.position.set(0, 0.5, 0);
    this.spotlight.castShadow = true;
    this.spotlight.shadow.mapSize.set(512, 512);
    this.spotlight.shadow.radius = 4;
    this.scene.add(this.spotlight);
    this.scene.add(this.spotlight.target);

    // Top fill light
    const topLight = new THREE.DirectionalLight(0xffffff, 0.6);
    topLight.position.set(0, 6, 0);
    this.scene.add(topLight);

    // Ambient
    const ambient = new THREE.AmbientLight(0xffffff, 0.25);
    this.scene.add(ambient);

    // Generate env map for reflections
    const pmremGenerator = new THREE.PMREMGenerator(this.shared.gl);
    pmremGenerator.compileCubemapShader();
    const envScene = new THREE.Scene();
    envScene.background = new THREE.Color(0x080810);
    const envLight1 = new THREE.PointLight(0x4422aa, 3, 20);
    envLight1.position.set(-5, 3, -5);
    envScene.add(envLight1);
    const envLight2 = new THREE.PointLight(0x002244, 2, 20);
    envLight2.position.set(5, 2, 5);
    envScene.add(envLight2);
    const envLight3 = new THREE.PointLight(0x331111, 2, 20);
    envLight3.position.set(0, 5, 0);
    envScene.add(envLight3);
    this.envMap = pmremGenerator.fromScene(envScene, 0.04).texture;
    pmremGenerator.dispose();
    this.scene.environment = this.envMap;

    this.clock = new THREE.Clock();

    // Register as a view on the shared renderer
    this.viewId = this.shared.addView({
      scene: this.scene,
      camera: this.camera,
      container: this.container,
      priority: 10, // Higher priority = renders on top when multiple views exist
      onBeforeRender: () => this.update(),
    });

    // Load dice model
    this.ready = this.loadDice();
  }

  private async loadDice(): Promise<void> {
    const loader = new GLTFLoader();

    try {
      const gltf = await loader.loadAsync(DICE_GLB_PATH);
      const template = gltf.scene;

      const box = new THREE.Box3().setFromObject(template);
      const center = box.getCenter(new THREE.Vector3());
      template.position.sub(center);
      template.scale.setScalar(DICE_SCALE);

      template.traverse((node) => {
        if (!(node as THREE.Mesh).isMesh) return;
        const mesh = node as THREE.Mesh;
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        const mat = mesh.material as THREE.MeshStandardMaterial;
        if (mat.name === 'body') {
          mat.color.set(0x8b0000);
          mat.roughness = 0;
          mat.metalness = 0.1;
          mat.envMapIntensity = 1.5;
        } else if (mat.name === 'circles') {
          mat.color.set(0xffffff);
          mat.roughness = 0;
          mat.emissive.set(0xffffff);
          mat.emissiveIntensity = 1;
          mat.envMapIntensity = 0.5;
        }
      });

      const unitSize = 1.0;

      // Die 1 (bottom-left): show 6 front
      const die1 = template.clone(true);
      die1.position.set(-0.52, unitSize / 2, 0.2);
      die1.rotation.y = 0.15;
      die1.children[0].rotation.x = -Math.PI;
      die1.children[0].rotation.y = Math.PI / 2;
      this.diceGroup.add(die1);

      // Die 2 (bottom-right): show 5 front
      const die2 = template.clone(true);
      die2.position.set(0.52, unitSize / 2, 0.2);
      die2.rotation.y = -0.1;
      die2.children[0].rotation.x = Math.PI / 2;
      die2.children[0].rotation.y = -Math.PI / 2;
      this.diceGroup.add(die2);

      // Die 3 (top): show 4 front
      const die3 = template.clone(true);
      die3.position.set(0, unitSize / 2 + unitSize * 0.95, -0.15);
      die3.rotation.y = 0.3;
      die3.rotation.z = 0.05;
      die3.children[0].rotation.x = -Math.PI / 2;
      this.diceGroup.add(die3);
    } catch (err) {
      console.warn('[LoginDiceScene] Failed to load dice GLB:', err);
    }
  }

  private update(): void {
    const elapsed = this.clock.getElapsedTime();

    // Gentle spotlight pan
    const angle = elapsed * SPOT_PAN_SPEED;
    this.spotlight.position.set(
      1.6 + Math.sin(angle) * SPOT_PAN_RADIUS,
      5 + Math.cos(angle * 0.7) * SPOT_PAN_RADIUS * SPOT_PAN_Y_RATIO,
      5.1,
    );
  }

  dispose(): void {
    // Remove from shared renderer
    if (this.viewId) {
      this.shared.removeView(this.viewId);
      this.viewId = null;
    }

    this.envMap?.dispose();
    this.envMap = null;

    this.scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material?.dispose();
        }
      }
    });
  }
}
